import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ExportButton } from "@/components/export-button";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, type PageMeta } from "@/lib/list-params";
import { titleCase, useFormat, useToday } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { resolveEstate, resolveProperty, searchEstates, searchProperties } from "@/lib/pickers";
import { SearchSelect } from "@/components/search-select";
import { useSession } from "@/lib/session";
import { useActiveModule } from "@/lib/module-rail";
import { Alert, Badge, Button, Card, CardTitle, EmptyState, ErrorState, Field, Input, PageHeader, Select, Spinner, Table } from "@/components/ui";
import { MoneyInput } from "@/components/money-input";

interface Row {
  id: string;
  category: string;
  description: string;
  amount: number;
  incurredOn: string;
  paidBy: string;
  recoverable: boolean;
  propertyId: string | null;
  propertyName: string | null;
  estateId: string | null;
  estateName: string | null;
  vendorName: string | null;
}

/**
 * This screen is shared by both modules and answers for the one in force.
 *
 * A cost is spent on a subject, and the two modules do not have the same subject: a letting cost
 * is spent on a building, a development cost on a scheme. Everything else about the screen — the
 * categories, the date, who paid, whether it is recoverable — is identical, so this is one screen
 * with one word changed rather than two screens that drift apart. What must NOT happen is the
 * development rail offering a picker full of letting buildings, or a development cost linking out
 * to `/app/properties/…` and quietly moving somebody to the other module: that is exactly the
 * crossing ADR-0159 exists to stop.
 */

export function ExpensesPage() {
  const { vocab, can, modules } = useSession();
  const active = useActiveModule(modules);
  const developing = active === "developing";
  const fmt = useFormat();
  const today = useToday();
  const [adding, setAdding] = useState(false);
  const [subjectId, setSubjectId] = useState("");

  /* The one word that changes. `vocab.property` is the agency's own name for a building; a scheme
     is called a Property on the development rail too, so the label is the same word and only the
     list behind it differs. */
  const subjectLabel = titleCase(vocab.property);

  const list = useListParams();

  const { data, isLoading, error, refetch } = useQuery({
    /* The module is in the key. Without it the two rails share one cached page, and switching
       module shows the other module's costs until a refetch lands. */
    queryKey: ["expenses", active, list.q, list.page],
    queryFn: () =>
      api.get<{ expenses: Row[]; categories: string[]; page: PageMeta }>("/expenses", {
        q: list.q,
        page: list.page,
      }),
    placeholderData: (previous) => previous,
  });

  const create = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post("/expenses", input),
    invalidates: [["expenses"], ["property"], ["estate"]],
    onSuccess: () => {
      setAdding(false);
      setSubjectId("");
    },
  });

  const rows = data?.expenses ?? [];
  const total = rows.reduce((sum, row) => sum + row.amount, 0);
  const recoverable = rows.filter((row) => row.recoverable).reduce((sum, row) => sum + row.amount, 0);

  return (
    <div>
      <PageHeader
        title="Expenses"
        subtitle={
          developing
            ? `${fmt.money(total)} recorded on this page`
            : `${fmt.money(total)} recorded · ${fmt.money(recoverable)} recoverable from ${vocab.landlords}`
        }
        description={
          developing
            ? `What has been spent on a scheme — site works, professional fees, materials, levies. Recorded against the ${vocab.property} it was spent on, so a scheme's costs can be read against what it has sold.`
            : `What the agency has spent on a ${vocab.property} — repairs, levies, wages. Mark it recoverable and it is deducted from what you remit to the ${vocab.landlord}.`
        }
        actions={
          <>
            <ExportButton kind="expenses" />
            {can("expense.edit") ? <Button onClick={() => setAdding((v) => !v)}>{adding ? "Close" : "Add an expense"}</Button> : null}
          </>
        }
      />

      {adding ? (
        <Card className="mb-5">
          <CardTitle
            hint={
              developing
                ? "Recorded against the scheme it was spent on."
                : `Recoverable expenses are deducted from the ${vocab.landlord}'s remittance.`
            }
          >
            New expense
          </CardTitle>
          {create.errorMessage ? <Alert tone="danger">{create.errorMessage}</Alert> : null}
          <form
            className="grid gap-4 sm:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault();
              const form = new FormData(event.currentTarget);
              const subject = String(form.get("subjectId") || "") || null;
              create.mutate({
                /* One subject, chosen by the module standing here. Sending both is refused by the
                   server and by the database — a cost is spent on one thing. */
                propertyId: developing ? null : subject,
                estateId: developing ? subject : null,
                category: String(form.get("category")),
                description: String(form.get("description")),
                amount: String(form.get("amount")),
                incurredOn: String(form.get("incurredOn")),
                /* Neither of these means anything on a scheme: a development cost is the
                   developer's own, and there is no landlord to remit to or recover from. The
                   server defaults both, so they are simply not sent. */
                ...(developing ? {} : { paidBy: String(form.get("paidBy")), recoverable: form.get("recoverable") === "on" }),
              });
            }}
          >
            <Field label={subjectLabel} htmlFor="subjectId">
              <SearchSelect
                id="subjectId"
                name="subjectId"
                value={subjectId}
                onChange={(next) => setSubjectId(next)}
                search={developing ? searchEstates : searchProperties}
                resolve={developing ? resolveEstate : resolveProperty}
                placeholder="Not linked"
                emptyMessage={`No ${vocab.property} matched.`}
              />
            </Field>
            <Field label="Category" htmlFor="category" required>
              <Select id="category" name="category" required>
                {(data?.categories ?? []).map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </Select>
            </Field>
            <Field label="Amount" htmlFor="amount" required>
              <MoneyInput id="amount" name="amount" required />
            </Field>
            <Field label="Description" htmlFor="description" required>
              <Input id="description" name="description" required placeholder="Borehole pump repair" />
            </Field>
            <Field label="Date" htmlFor="incurredOn" required>
              <Input id="incurredOn" name="incurredOn" type="date" defaultValue={today} required />
            </Field>
            {developing ? null : (
              <>
                <Field label="Paid by" htmlFor="paidBy">
                  <Select id="paidBy" name="paidBy" defaultValue="agency">
                    <option value="agency">The agency</option>
                    <option value="landlord">The {vocab.landlord}</option>
                  </Select>
                </Field>
                <label className="flex items-center gap-2 text-sm sm:col-span-3">
                  <input type="checkbox" name="recoverable" defaultChecked className="size-4 rounded border-ink-300" />
                  Deduct from the {vocab.landlord}'s remittance
                </label>
              </>
            )}
            <div className="sm:col-span-3">
              <Button type="submit" disabled={create.isPending}>{create.isPending ? "Saving…" : "Save expense"}</Button>
            </div>
          </form>
        </Card>
      ) : null}

      <Card padded={false}>
        <div className="border-b border-ink-200 p-3">
          <SearchBox params={list} placeholder={"Search description, category or vendor…"} />
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No expenses recorded."
            hint={
              developing
                ? "They are what a scheme cost, and they sit against what it has sold."
                : `They feed the ${vocab.landlord} statement and the property's profit and loss.`
            }
          />
        ) : (
          <Table
            head={
              <tr>
                <th>Date</th>
                <th>{subjectLabel}</th>
                <th>Category</th>
                <th>Description</th>
                {developing ? null : <th>Paid by</th>}
                <th className="num">Amount</th>
              </tr>
            }
          >
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="text-ink-500">{fmt.date(row.incurredOn)}</td>
                <td>
                  {/* Each subject links inside its own module. A development cost linking to
                      `/app/properties/…` would move the reader to the letting rail without asking,
                      which is the crossing ADR-0159 forbids. */}
                  {row.estateId ? (
                    <Link to={`/app/development/properties/${row.estateId}`} className="link">{row.estateName}</Link>
                  ) : row.propertyId ? (
                    <Link to={`/app/properties/${row.propertyId}`} className="link">{row.propertyName}</Link>
                  ) : (
                    <span className="text-ink-400">—</span>
                  )}
                </td>
                <td>{row.category}</td>
                <td className="text-ink-500">{row.description}</td>
                {developing ? null : (
                  <td>
                    <Badge tone={row.recoverable ? "warn" : "muted"}>
                      {row.paidBy === "agency" ? "Agency" : titleCase(vocab.landlord)}
                      {row.recoverable ? " · recoverable" : ""}
                    </Badge>
                  </td>
                )}
                <td className="num tabular">{fmt.money(row.amount)}</td>
              </tr>
            ))}
          </Table>
        )}

        <Pagination meta={data?.page} params={list} noun={"expenses"} />
      </Card>
    </div>
  );
}
